import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useTaxYear } from '@/contexts/TaxYearContext';
import { FORM_FILING_TYPE, type FormFilingType } from '@/constants/formConstants';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { CopyFromPriorYearDialog } from '@/components/tax-form/CopyFromPriorYearDialog';
import { NewBusinessFormDialog } from '@/components/tax-form/NewBusinessFormDialog';
import { createForm, deleteForm, listForms } from '@/services/formsApi';
import { Briefcase, Trash2, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { FormListItem } from '@/services/formsApi';

export default function TaxFormLandingPage() {
  const { selectedYear } = useTaxYear();
  const navigate = useNavigate();
  const [forms, setForms] = useState<FormListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmFormId, setDeleteConfirmFormId] = useState<string | null>(null);
  const [showNewBusinessDialog, setShowNewBusinessDialog] = useState(false);
  const [showCopyFromPriorDialog, setShowCopyFromPriorDialog] = useState(false);

  const loadForms = async () => {
    try {
      const list = await listForms({ taxYear: selectedYear });
      setForms(list);
    } catch {
      toast.error('Failed to load tax forms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, [selectedYear]);

  const listPriorIndividualForms = useCallback(async (beforeTaxYear: number) => {
    return listForms({
      beforeTaxYear,
      filingType: FORM_FILING_TYPE.INDIVIDUAL,
    });
  }, []);

  const handleStartNew = async (filingType: FormFilingType) => {
    if (filingType === FORM_FILING_TYPE.BUSINESS) {
      setShowNewBusinessDialog(true);
      return;
    }
    setShowCopyFromPriorDialog(true);
  };

  const handleCreateIndividualForm = async (options: {
    mode: 'blank' | 'copy';
    copyPersonalInfoFromFormId?: string;
  }) => {
    setCreating(true);
    try {
      const created = await createForm({
        filingType: FORM_FILING_TYPE.INDIVIDUAL,
        taxYear: selectedYear,
        ...(options.mode === 'copy' && options.copyPersonalInfoFromFormId
          ? { copyPersonalInfoFromFormId: options.copyPersonalInfoFromFormId }
          : {}),
      });
      toast.success(
        options.mode === 'copy'
          ? 'Tax form created with prior-year personal info'
          : 'Tax form created'
      );
      setShowCopyFromPriorDialog(false);
      navigate(`/tax-form/${created.id}`);
    } catch {
      toast.error('Failed to create tax form');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateBusinessForm = async (formName: string) => {
    setCreating(true);
    try {
      const created = await createForm({
        filingType: FORM_FILING_TYPE.BUSINESS,
        taxYear: selectedYear,
        formName: formName || undefined,
      });
      toast.success('Tax form created');
      setShowNewBusinessDialog(false);
      navigate(`/tax-form/${created.id}`);
    } catch {
      toast.error('Failed to create tax form');
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmDeleteBusinessForm = async () => {
    const formId = deleteConfirmFormId;
    if (!formId) return;
    setDeletingId(formId);
    setDeleteConfirmFormId(null);
    try {
      await deleteForm(formId);
      toast.success('Business form deleted');
      loadForms();
    } catch {
      toast.error('Failed to delete form');
    } finally {
      setDeletingId(null);
    }
  };

  /** One individual form per year */
  const individualForm = !loading
    ? forms.find((f) => f.filingType === FORM_FILING_TYPE.INDIVIDUAL) ?? null
    : null;
  /** All business forms for the year (user can have any number) */
  const businessForms = !loading
    ? forms.filter((f) => f.filingType === FORM_FILING_TYPE.BUSINESS)
    : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Tax Filing Form</h1>
        <p className="text-muted-foreground mt-1">
          You can have one Individual and any number of Business forms per year. Both can exist at the same time. Progress is saved per section.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Individual: one form per year */}
        <Card className="overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-lg">Individual</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Personal income tax (1040). Personal info, income, deductions, and more.
                </p>
                {individualForm ? (
                  <Button
                    className="mt-4"
                    variant="outline"
                    onClick={() => navigate(`/tax-form/${individualForm.id}`)}
                  >
                    Continue editing
                  </Button>
                ) : (
                  <Button
                    className="mt-4"
                    onClick={() => handleStartNew(FORM_FILING_TYPE.INDIVIDUAL)}
                    disabled={creating}
                  >
                    {creating ? 'Creating…' : 'Start new Individual form'}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Business: any number of forms per year */}
        <Card className="overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <Briefcase className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-lg">Business</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Business tax (Schedule C / entity). Business details, income, expenses, vehicle.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  {businessForms.map((form, index) => (
                    <div key={form.id} className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate font-medium" title={form.formName?.trim() || `Business form ${index + 1}`}>
                        {form.formName?.trim() || `Business form ${index + 1}`}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/tax-form/${form.id}`)}
                      >
                        Continue editing
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-destructive hover:text-destructive"
                        onClick={() => setDeleteConfirmFormId(form.id)}
                        disabled={deletingId === form.id}
                        title="Delete this business form"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    onClick={() => handleStartNew(FORM_FILING_TYPE.BUSINESS)}
                    disabled={creating}
                  >
                    {businessForms.length === 0 ? 'Start new Business form' : 'Add another Business form'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <CopyFromPriorYearDialog
        open={showCopyFromPriorDialog}
        onOpenChange={setShowCopyFromPriorDialog}
        selectedYear={selectedYear}
        listPriorForms={listPriorIndividualForms}
        onCreate={handleCreateIndividualForm}
        loading={creating}
      />

      <NewBusinessFormDialog
        open={showNewBusinessDialog}
        onOpenChange={setShowNewBusinessDialog}
        defaultFormName={`Business form ${businessForms.length + 1}`}
        onCreate={handleCreateBusinessForm}
        loading={creating}
      />

      <ConfirmDialog
        open={deleteConfirmFormId !== null}
        onOpenChange={(open) => !open && setDeleteConfirmFormId(null)}
        title="Delete business form"
        description="Are you sure you want to delete this business form? This cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        loading={deletingId !== null}
        onConfirm={handleConfirmDeleteBusinessForm}
      />
    </div>
  );
}
